/**
 * Mapping helpers: FHIR resources ↔ intake UI view models.
 *
 * Each mapper converts Medplum/FHIR resources into the shapes the existing
 * UI components already consume (MockProvider, OfficeLocation, time slots).
 */

import type { Location, Practitioner, Slot } from '@medplum/fhirtypes';
import type { MockProvider } from './mockProviders';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { INTAKE_EXT_URLS } from './intakeFhirConstants';
import { formatDateOnlyLocal } from './intakeDateUtils';

// ---------------------------------------------------------------------------
// Helper: extract a single string extension value
// ---------------------------------------------------------------------------

function extString(
  resource: { extension?: { url: string; valueString?: string; valueBoolean?: boolean; valueUrl?: string }[] },
  url: string,
): string | undefined {
  const ext = resource.extension?.find((e) => e.url === url);
  return ext?.valueString ?? ext?.valueUrl;
}

function extBoolean(
  resource: { extension?: { url: string; valueBoolean?: boolean }[] },
  url: string,
  fallback: boolean,
): boolean {
  const ext = resource.extension?.find((e) => e.url === url);
  return ext?.valueBoolean ?? fallback;
}

// ---------------------------------------------------------------------------
// Practitioner → MockProvider (intake provider card view model)
// ---------------------------------------------------------------------------

export function practitionerToIntakeProvider(p: Practitioner): MockProvider {
  const displayName = formatPractitionerPreferredLabel(p);

  const credentials = extString(p, INTAKE_EXT_URLS.credentials) ?? '';
  const headshotUrl = extString(p, INTAKE_EXT_URLS.headshotUrl) ?? null;
  const shortBio = extString(p, INTAKE_EXT_URLS.shortBio) ?? '';
  const longBio = extString(p, INTAKE_EXT_URLS.longBio) ?? '';
  const acceptsInsurance = extBoolean(p, INTAKE_EXT_URLS.acceptsInsurance, true);
  const takingNewClients = extBoolean(p, INTAKE_EXT_URLS.takingNewClients, true);

  const formatsRaw = extString(p, INTAKE_EXT_URLS.sessionFormats) ?? 'virtual';
  const format = formatsRaw
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is 'in-person' | 'virtual' => s === 'in-person' || s === 'virtual');
  if (format.length === 0) format.push('virtual');

  const gender: 'male' | 'female' =
    p.gender === 'male' ? 'male' : 'female';

  const specialties = (p.qualification ?? [])
    .map((q) => q.code?.text)
    .filter((t): t is string => Boolean(t));

  const languages = (p.communication ?? [])
    .map((c) => c.text ?? c.coding?.[0]?.display)
    .filter((t): t is string => Boolean(t));

  const location =
    p.address?.[0]
      ? `${p.address[0].city ?? ''}, ${p.address[0].state ?? ''}`.replace(/^, |, $/, '')
      : '';
  const zip = p.address?.[0]?.postalCode ?? '';

  return {
    id: p.id!,
    name: displayName,
    credentials,
    headshotUrl,
    gender,
    specialties,
    format,
    location,
    zip,
    acceptsInsurance,
    shortBio,
    longBio,
    languages,
    takingNewClients,
  };
}

// ---------------------------------------------------------------------------
// Location → OfficeLocation (intake location card view model)
// ---------------------------------------------------------------------------

export interface IntakeLocation {
  id: string;
  name: string;
  address: string;
  distanceMiles: number | null;
}

export function locationToIntakeLocation(loc: Location): IntakeLocation {
  const addr = loc.address;
  const addressParts = [
    addr?.line?.join(', '),
    addr?.city,
    addr?.state,
    addr?.postalCode,
  ].filter(Boolean);

  return {
    id: loc.id!,
    name: loc.name ?? 'Office',
    address: addressParts.join(', '),
    distanceMiles: null,
  };
}

// ---------------------------------------------------------------------------
// Slot → booking time picker data
// ---------------------------------------------------------------------------

export interface BookableSlot {
  slotId: string;
  start: string;
  end: string;
  /** HH:mm for the time grid */
  timeKey: string;
  /** YYYY-MM-DD for the date picker */
  dateKey: string;
}

export function slotsToBookableSlots(slots: Slot[]): BookableSlot[] {
  return slots
    .filter((s) => s.status === 'free' && s.start && s.end)
    .map((s) => {
      const start = new Date(s.start!);
      // Local calendar day to match the local timeKey below (was UTC day, which
      // disagreed with local times for evening slots).
      const dateKey = formatDateOnlyLocal(start);
      const hh = String(start.getHours()).padStart(2, '0');
      const mm = String(start.getMinutes()).padStart(2, '0');
      return {
        slotId: s.id!,
        start: s.start!,
        end: s.end!,
        timeKey: `${hh}:${mm}`,
        dateKey,
      };
    })
    .sort((a, b) => a.start.localeCompare(b.start));
}

/**
 * Group bookable slots by date for the calendar/time-grid UI.
 */
export function groupSlotsByDate(
  slots: BookableSlot[],
): Map<string, BookableSlot[]> {
  const map = new Map<string, BookableSlot[]>();
  for (const s of slots) {
    const arr = map.get(s.dateKey) ?? [];
    arr.push(s);
    map.set(s.dateKey, arr);
  }
  return map;
}
